// The labels the admin's previews write on a keycap, out of KeycapOverlay and
// ProcessOrderScreen (chain 9 of the backfill, Oct 2026; each held a copy of the two labels):
// the size a keycap is labelled with and the form that sets it apart, the two legends the
// layout editor's preview shows them as on every keycap, and the printing-file preview's
// label text with the paint that keeps a homing-bar mark red when the labels are switched on.
import { getContrastingColor, isRedishColor } from './colorHelpers';

export const keycapSizeLabel = (keycap) =>
  keycap.isIsoEnter
    ? 'Enter'
    : keycap.row === 'Uniform' && keycap.width < keycap.height
      ? `${keycap.height}x${keycap.width}`
      : `${keycap.width}x${keycap.height}`; // the size as the label says it: Enter for the ISO Enter, a standing keycap of the uniform profile (the XDA 1x2, which lies on its mold slot) as its lying size, else width x height

export const keycapExtraLabel = (keycap) =>
  keycap.isConvex && keycap.isOffCentered
    ? 'Convex (off-centered)'
    : keycap.isConvex
      ? 'Convex'
      : keycap.stepped
        ? 'Stepped'
        : keycap.hasHomingBar
          ? 'home'
          : keycap.row === 'Uniform'
            ? null
            : keycap.row && keycap.row.length > 2
              ? keycap.row
              : null; // what sets the keycap apart from a plain one of its size, the first that applies, else its row when the row name says more than a number (R2-3, the ISO Enter's), and null for a plain keycap of a plain row or of the uniform profile

export const layoutEditorLabelTexts = (keycap) => ({
  1: {
    text: keycapSizeLabel(keycap),
    fontFamilyName: 'poppins',
    style: {
      fontFamily: 'Poppins-Regular',
      fontSize: 3.8,
    },
    position: {
      isoSection: 'main',
      gridStructure: '3x3',
      gridRow: 3,
      gridColumn: 3,
      cellAlignment: 'bottomRight',
      boundingBox: 'compact',
    },
    isActiveStates: {
      fill: false,
      highlight: false,
      underline: false,
      strikethrough: false,
      stroke: false,
      textSpacing: false,
    },
    visible: true,
  },
  2: {
    text: keycapExtraLabel(keycap),
    fontFamilyName: 'poppins',
    style: {
      fontFamily: 'Poppins-Regular',
      fontSize: 3.5,
    },
    position: {
      isoSection: 'main',
      gridStructure: '3x3',
      gridRow: 2,
      gridColumn: 2,
      cellAlignment: 'center',
      boundingBox: 'compact',
    },
    isActiveStates: {
      fill: false,
      highlight: false,
      underline: false,
      strikethrough: false,
      stroke: false,
      textSpacing: false,
    },
    visible: true,
  },
}); // the two legends the layout editor's preview puts on every keycap in place of a design's texts: the size at the bottom right in Poppins 3.8, the extra label at the centre in 3.5, its text null when the keycap has none

export const printingLabel = (keycap) => {
  const sizeLabel = keycapSizeLabel(keycap);
  const extraLabel = keycapExtraLabel(keycap);
  return extraLabel ? `${sizeLabel} (${extraLabel})` : sizeLabel;
}; // the label the printing-file preview prints on a keycap when the labels are switched on: the size, with the extra label in brackets

export const printingLabelPaint = (keycap) => {
  const homingBarMark = keycapExtraLabel(keycap) === 'home';
  const homingBarMarkOnReddish = homingBarMark && isRedishColor(keycap.color);
  return {
    fontSize: homingBarMarkOnReddish ? 4 : 4.5,
    fill: homingBarMark ? 'red' : getContrastingColor(keycap.color),
    stroke: homingBarMarkOnReddish ? getContrastingColor(keycap.color) : 'none',
    strokeWidth: homingBarMarkOnReddish ? 1 : 0,
  };
}; // how the preview paints the label: black or white against the keycap's colour, a homing-bar mark red, and on a reddish keycap smaller with an outline in the contrasting colour (drawn under the fill: paintOrder stroke in the JSX), so the red mark stays visible there
